import {
  DayPartSections,
  ID,
  ReorderItem,
  ReorderResult,
  Task,
  TaskListItemTypes,
  WeekDays,
} from '@griply/types';
import { endOfDay, isBefore, isSameDay } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';

import { HALF_HOUR } from '../constants';
import { getDueTaskPlanning } from '../get-task-planning';
import { createPlannedOccurrenceForDate } from '../schedules';
import { filterNewTaskChildIds } from './filter-new-task-child-ids';
import { findParentItem } from './find-parent-item';
import { getDayPartStartTime } from './get-day-part-start-time';

export type OnDayPartReorderOptions = {
  updatedItems: readonly ReorderItem[];
  oldItems: readonly ReorderItem[];
  tasksMap: Record<ID, Task>;
  today: Date;
  weekStartsOn: WeekDays;
};

export const onDayPartReorder = ({
  updatedItems,
  oldItems,
  tasksMap,
  today,
  weekStartsOn,
}: OnDayPartReorderOptions): ReorderResult => {
  const updatedTasks: Task[] = [];
  const updatedSections: ReorderResult['updatedSections'] = [];

  updatedItems.forEach((item) => {
    if (item.type === TaskListItemTypes.Task) {
      if (!tasksMap[item.id]) {
        return;
      }

      const task = tasksMap[item.id];
      const taskToUpdate = cloneDeep(task);

      const newSectionItem = findParentItem(updatedItems, item.id);
      const oldSectionItem = findParentItem(oldItems, item.id);

      if (!newSectionItem || !oldSectionItem) {
        return;
      }

      const newSectionId = newSectionItem.id as DayPartSections;
      const oldSectionId = oldSectionItem.id as DayPartSections;

      if (newSectionId === oldSectionId) {
        return;
      }

      if (newSectionId === DayPartSections.Overdue) {
        return;
      }

      const newStartTime = getDayPartStartTime(newSectionId);

      if (task.schedules?.length) {
        const planning = getDueTaskPlanning(task, {
          date: today,
          today,
          weekStartsOn,
        });
        const plannedToday = planning.find(({ date }) =>
          isSameDay(date, today),
        );
        const overduePlannedOccurrence = plannedToday
          ? undefined
          : planning.find(({ date }) => isBefore(date, today));

        const newTimeslot = {
          startTime: newStartTime,
          duration:
            plannedToday?.timeslot?.duration ??
            overduePlannedOccurrence?.timeslot?.duration ??
            HALF_HOUR,
        };

        taskToUpdate.schedules = createPlannedOccurrenceForDate(
          task.schedules,
          today,
          {
            timeslot: newTimeslot,
            originalDate:
              overduePlannedOccurrence?.date ?? plannedToday?.date ?? today,
            weekStartsOn,
            today,
          },
        );
        updatedTasks.push(taskToUpdate);
        return;
      }

      const newTimeslot = {
        startTime: newStartTime,
        duration: task.timeslot?.duration ?? HALF_HOUR,
      };
      taskToUpdate.endStrategy = {
        deadline: endOfDay(today),
        completionCount: task.endStrategy?.completionCount ?? null,
      };
      taskToUpdate.timeslot =
        newStartTime || newTimeslot.duration ? newTimeslot : null;
      updatedTasks.push(taskToUpdate);
      return;
    }

    if (item.type === TaskListItemTypes.DayPart) {
      updatedSections.push({
        id: item.id,
        taskSorting: filterNewTaskChildIds(item.children),
      });
    }
  });

  return { updatedSections, updatedTasks };
};
